import { useState, useEffect, useCallback } from 'react';

// Utility function for debouncing
const debounce = (fn, delay) => {
  let timeoutId;
  return (...args) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      fn(...args);
    }, delay);
  };
};

const useTooltip = (delay = 50) => { // Add configurable delay parameter
  const [tooltipData, setTooltipData] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [hoveredItem, setHoveredItem] = useState(null);

  // Cleanup effect
  useEffect(() => {
    return () => {
      setTooltipData(null);
      setTooltipPosition({ x: 0, y: 0 });
      setHoveredItem(null);
    };
  }, []);

  // Create stable references to state setters
  const setTooltipDataStable = useCallback((data) => {
    setTooltipData(data);
  }, []);

  const setTooltipPositionStable = useCallback((position) => {
    setTooltipPosition(position);
  }, []);

  const setHoveredItemStable = useCallback((item) => {
    setHoveredItem(item);
  }, []);

  // Create debounced versions of the handlers
  const debouncedSetTooltip = useCallback(
    debounce((data, position, item) => {
      setTooltipDataStable(data);
      setTooltipPositionStable(position);
      setHoveredItemStable(item);
    }, delay),
    [setTooltipDataStable, setTooltipPositionStable, setHoveredItemStable, delay]
  );

  const handleHover = useCallback((item, position) => {
    debouncedSetTooltip(item, position, item);
  }, [debouncedSetTooltip]);

  const handleMouseOut = useCallback(() => {
    // Clear the timeout to prevent any pending hover updates
    debouncedSetTooltip(null, { x: 0, y: 0 }, null);
  }, [debouncedSetTooltip]);

  return {
    tooltipData,
    tooltipPosition,
    hoveredItem,
    handleHover,
    handleMouseOut
  };
};

export default useTooltip; 
